import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReactFlow } from 'reactflow';

import AuthContext from './AuthContext';
import { EditorUIProvider, useEditorUI } from './EditorUIContext';
import useCanvasOps from '../hooks/useCanvasOps';
import useCollabSync from '../hooks/useCollabSync';
import CLIENT_ID from '../utils/clientId';
import { newObjectId } from '../utils/objectId';
import { checkValidity } from '../utils/utils';
import * as CanvasAPI from '../services/CanvasServices';
import * as ScenarioAPI from '../services/ScenarioServices';
import tutorialBus from '../tutorials/engine/tutorialBus';

/**
 * CanvasContext — the draft graph and the operations that change it.
 *
 * The canvas IS the draft. `node.data` holds every authoring field a Situation
 * used to hold, and `edge.data` every Choice field; publishing materializes them
 * into the documents users read.
 *
 */

const CanvasContext = createContext();

/** A brand-new situation, before the author has typed anything into it. */
const BLANK_NODE_DATA = {
    title: '',
    text: '',
    endpointType: '',
    fillColor: '#ffffff',
    borderColor: '#000000',
};

const CanvasDataProvider = ({ children }) => {
    const { scenarioId } = useParams();
    const { userId: authUserId } = useContext(AuthContext);
    const { getZoom, setCenter, project } = useReactFlow();
    const {
        selectItem,
        dropSelectionsOf,
        setPermissionLevel,
        reactFlowWrapperRef,
    } = useEditorUI();

    const [scenario, setScenario] = useState(null);
    const [loading, setLoading] = useState(true);
    const [graphErrors, setGraphErrors] = useState(null);
    const [publishing, setPublishing] = useState(false);

    const ops = useCanvasOps({ scenarioId, enabled: true });
    const {
        nodes,
        edges,
        groups,
        keys,
        tableOfContents,
        dispatchOps,
        flushOps,
        setGraph,
        loadCanvasDocument,
        applyRemoteOps,
    } = ops;


    const [lastRemoteEdit, setLastRemoteEdit] = useState(null);

    const mergeScenarioFields = useCallback((fields) => {
        if (!fields || typeof fields !== 'object') return;
        const present = Object.fromEntries(
            Object.entries(fields).filter(([, value]) => value !== undefined)
        );
        if (Object.keys(present).length === 0) return;
        setScenario((current) => (current ? { ...current, ...present } : current));
    }, []);


    const handleRemoteOps = useCallback(
        (payload) => {
            applyRemoteOps(payload);
            if (payload?.userId && payload.userId !== authUserId) {
                setLastRemoteEdit({ userId: payload.userId, at: Date.now() });
            }
        },
        [applyRemoteOps, authUserId]
    );

    const handlePublished = useCallback(
        ({ publishedVersion: remotePublished, scenario: fields }) => {
            if (typeof remotePublished === 'number') ops.setPublishedVersion(remotePublished);
            mergeScenarioFields(fields);
        },
        [ops, mergeScenarioFields]
    );

    const handleScenarioMeta = useCallback(
        ({ fields }) => mergeScenarioFields(fields),
        [mergeScenarioFields]
    );

    useCollabSync({
        scenarioId,
        enabled: true,
        applyRemoteOps: handleRemoteOps,
        onPublished: handlePublished,
        onScenarioMeta: handleScenarioMeta,
    });

    // Loaders

    useEffect(() => {
        if (!scenarioId) return undefined;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const [scenarioData, canvas] = await Promise.all([
                    ScenarioAPI.getScenario(scenarioId),
                    CanvasAPI.getCanvas(scenarioId).catch((err) =>
                        err?.response?.status === 404 ? CanvasAPI.createCanvas(scenarioId) : Promise.reject(err)
                    ),
                ]);
                if (cancelled) return;

                setScenario(scenarioData);
                setPermissionLevel(scenarioData.creator === authUserId ? 'creator' : 'collaborator');
                loadCanvasDocument(canvas);
            } catch (err) {
                if (!cancelled) console.error('Error initializing canvas:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scenarioId, authUserId, loadCanvasDocument, setPermissionLevel]);

    // Lookups

    const getNode = useCallback((nodeId) => nodes.find((node) => node.id === nodeId) || null, [nodes]);
    const getEdge = useCallback((edgeId) => edges.find((edge) => edge.id === edgeId) || null, [edges]);

    const getOutgoingEdges = useCallback(
        (nodeId) => edges.filter((edge) => edge.source === nodeId),
        [edges]
    );

    const centerNodeInView = useCallback(
        (nodeId) => {
            const node = nodes.find((n) => n.id === nodeId);
            if (!node?.position) return;
            setCenter(node.position.x, node.position.y, { zoom: getZoom(), duration: 800 });
        },
        [nodes, setCenter, getZoom]
    );

    // Node/Situations

    const addSituation = useCallback(
        ({ position, data = {}, select = true } = {}) => {
            const id = newObjectId();
            dispatchOps({
                type: 'addNode',
                id,
                position: { x: position?.x ?? 0, y: position?.y ?? 0 },
                data: { ...BLANK_NODE_DATA, ...data },
            });
            if (select) selectItem('situation', id);
            // Announced here rather than from the drop handler because every way
            // of adding a situation — drag, mobile tap, keyboard, the outline —
            // comes through this function, and this is where the id is minted.
            tutorialBus.emit('situation:added', { id, data: { ...BLANK_NODE_DATA, ...data } });
            return id;
        },
        [dispatchOps, selectItem]
    );

    const addSituationAtCenter = useCallback(
        (endpointType = '') => {
            const bounds = reactFlowWrapperRef?.current?.getBoundingClientRect();
            const screenPoint = bounds
                ? { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 }
                : { x: window.innerWidth / 2, y: window.innerHeight / 2 };

            return addSituation({ position: project(screenPoint), data: { endpointType } });
        },
        [addSituation, project, reactFlowWrapperRef]
    );

    const updateSituation = useCallback(
        (nodeId, patch) => {
            if (!nodeId || !patch) return;
            dispatchOps({ type: 'updateNodeData', id: nodeId, data: patch });
        },
        [dispatchOps]
    );

    const deleteSituation = useCallback(
        (nodeId) => {
            dispatchOps({ type: 'removeNode', id: nodeId, rewireIncoming: true }, { immediate: true });
            dropSelectionsOf('situation', nodeId);
            tutorialBus.emit('situation:removed', { id: nodeId });
        },
        [dispatchOps, dropSelectionsOf]
    );

    const moveNodes = useCallback(
        (positions) => {
            if (!positions?.length) return;
            dispatchOps({ type: 'moveNodes', positions });
        },
        [dispatchOps]
    );

    const copySituation = useCallback(
        (nodeId) => {
            const original = getNode(nodeId);
            if (!original) return null;

            const newNodeId = newObjectId();
            const batch = [
                {
                    type: 'addNode',
                    id: newNodeId,
                    position: { x: original.position.x + 50, y: original.position.y + 50 },
                    data: {
                        ...BLANK_NODE_DATA,
                        endpointType: original.data?.endpointType || '',
                        isLogged: !!original.data?.isLogged,
                        isFreeResponse: !!original.data?.isFreeResponse,
                        isUserSubmitted: !!original.data?.isUserSubmitted,
                        autoplay: !!original.data?.autoplay,
                        sourcePosition: original.data?.sourcePosition || 'right',
                        targetPosition: original.data?.targetPosition || 'left',
                    },
                },
            ];

            for (const edge of edges) {
                if (edge.source !== nodeId || edge.target !== nodeId) continue;
                batch.push({
                    type: 'addEdge',
                    id: newObjectId(),
                    source: newNodeId,
                    target: newNodeId,
                    data: { title: '', text: '', correctness: edge.data?.correctness ?? null },
                });
            }

            dispatchOps(batch);
            selectItem('situation', newNodeId);
            return newNodeId;
        },
        [getNode, edges, dispatchOps, selectItem]
    );

    // Choices/Edges

    const addChoice = useCallback(
        ({ source, target, data = {}, select = true } = {}) => {
            if (!source || !target) return null;
            const id = newObjectId();
            dispatchOps({ type: 'addEdge', id, source, target, data: { title: '', text: '', ...data } });
            if (select) selectItem('choice', id);
            tutorialBus.emit('choice:added', { id, source, target });
            return id;
        },
        [dispatchOps, selectItem]
    );

    const addSituationWithChoice = useCallback(
        ({ source, position }) => {
            const nodeId = newObjectId();
            const edgeId = newObjectId();

            dispatchOps([
                {
                    type: 'addNode',
                    id: nodeId,
                    position: { x: position?.x ?? 0, y: position?.y ?? 0 },
                    data: { ...BLANK_NODE_DATA },
                },
                { type: 'addEdge', id: edgeId, source, target: nodeId, data: { title: '', text: '' } },
            ]);

            selectItem('situation', nodeId);
            return { nodeId, edgeId };
        },
        [dispatchOps, selectItem]
    );

    const updateChoice = useCallback(
        (edgeId, patch) => {
            if (!edgeId || !patch) return;
            dispatchOps({ type: 'updateEdgeData', id: edgeId, data: patch });
        },
        [dispatchOps]
    );

    const setChoiceTarget = useCallback(
        (edgeId, target) => {
            if (!edgeId || !target) return;
            dispatchOps({ type: 'setEdgeTarget', id: edgeId, target });
        },
        [dispatchOps]
    );

    const reorderChoices = useCallback(
        (nodeId, edgeIds) => {
            if (!nodeId || !edgeIds?.length) return;
            dispatchOps({ type: 'reorderEdges', source: nodeId, edgeIds });
        },
        [dispatchOps]
    );

    const deleteChoice = useCallback(
        (edgeId) => {
            dispatchOps({ type: 'removeEdge', id: edgeId }, { immediate: true });
            dropSelectionsOf('choice', edgeId);
            tutorialBus.emit('choice:removed', { id: edgeId });
        },
        [dispatchOps, dropSelectionsOf]
    );

    // Groups + Keys

    const getGroup = useCallback(
        (groupId) => (groupId ? groups.find((group) => group.id === String(groupId)) || null : null),
        [groups]
    );
    const getKey = useCallback(
        (keyId) => (keyId ? keys.find((key) => key.id === String(keyId)) || null : null),
        [keys]
    );

    const createGroup = useCallback(
        (data = {}) => {
            const id = newObjectId();
            dispatchOps({ type: 'addGroup', id, data });
            return id;
        },
        [dispatchOps]
    );

    const updateGroup = useCallback(
        (groupId, patch) => {
            if (!groupId || !patch) return;
            dispatchOps({ type: 'updateGroup', id: String(groupId), data: patch });
        },
        [dispatchOps]
    );

    const deleteGroup = useCallback(
        (groupId) => {
            dispatchOps({ type: 'removeGroup', id: String(groupId) }, { immediate: true });
            dropSelectionsOf('group', String(groupId));
        },
        [dispatchOps, dropSelectionsOf]
    );

    const createKey = useCallback(
        (data = {}) => {
            if (!data.name?.trim()) return null;
            const id = newObjectId();
            dispatchOps({ type: 'addKey', id, data });
            return id;
        },
        [dispatchOps]
    );

    const updateKey = useCallback(
        (keyId, patch) => {
            if (!keyId || !patch) return;
            dispatchOps({ type: 'updateKey', id: String(keyId), data: patch });
        },
        [dispatchOps]
    );

    const deleteKey = useCallback(
        (keyId) => {
            dispatchOps({ type: 'removeKey', id: String(keyId) }, { immediate: true });
            dropSelectionsOf('key', String(keyId));
        },
        [dispatchOps, dropSelectionsOf]
    );

    // Table of Contents

    const setTableOfContents = useCallback(
        (next) => {
            dispatchOps({ type: 'setTableOfContents', tableOfContents: next ?? null });
        },
        [dispatchOps]
    );

    // Media

    /**
     * Uploads a file, then records its URL with an ordinary data op.
     *
     * Splitting the two is the point: a failed upload leaves the graph exactly as
     * it was, instead of half-applying an edit the way the old combined
     * multipart endpoints could.
     *
     * Groups take the same path as nodes and edges now. They used to be the one
     * exception — a multipart PATCH straight onto the live document, which both
     * uploaded and mutated in a single request that could half-succeed.
     *
     * @param {{kind: 'image'|'audio'|'document', file: File, target: 'node'|'edge'|'group', id: string, field?: string}} params
     */
    const uploadMedia = useCallback(
        async ({ kind, file, target, id, field }) => {
            const key = field || kind;
            const currentUrl =
                target === 'edge'
                    ? getEdge(id)?.data?.[key]
                    : target === 'group'
                      ? getGroup(id)?.[key]
                      : getNode(id)?.data?.[key];

            const { url } = await CanvasAPI.uploadMedia(scenarioId, {
                kind,
                file,
                replacesUrl: currentUrl || undefined,
            });

            if (target === 'edge') updateChoice(id, { [key]: url });
            else if (target === 'group') updateGroup(id, { [key]: url });
            else updateSituation(id, { [key]: url });

            return url;
        },
        [scenarioId, getEdge, getNode, getGroup, updateChoice, updateGroup, updateSituation]
    );

    const clearMedia = useCallback(
        ({ target, id, field }) => {
            if (target === 'edge') updateChoice(id, { [field]: null });
            else if (target === 'group') updateGroup(id, { [field]: null });
            else updateSituation(id, { [field]: null });
        },
        [updateChoice, updateGroup, updateSituation]
    );

    // Draft Publish

    const publish = useCallback(async () => {
        setGraphErrors([]);

        const localErrors = checkValidity(nodes, edges);
        if (localErrors.length > 0) {
            setGraphErrors(localErrors);
            tutorialBus.emit('scenario:publishFailed', { scenarioId, errors: localErrors });
            return { published: false, errors: localErrors };
        }

        setPublishing(true);
        try {
            const flushError = await flushOps();
            if (flushError) {
                tutorialBus.emit('scenario:publishFailed', { scenarioId, message: flushError });
                return { published: false, message: flushError };
            }

            const result = await CanvasAPI.publishCanvas(scenarioId, { clientId: CLIENT_ID });
            setScenario((current) => ({ ...current, ...result.scenario }));
            ops.setPublishedVersion(result.publishedVersion);
            tutorialBus.emit('scenario:published', {
                scenarioId,
                publishedVersion: result.publishedVersion,
            });
            return { published: true, publishedVersion: result.publishedVersion };
        } catch (err) {
            const errors = err?.response?.data?.errors;
            if (errors) {
                setGraphErrors(errors);
                tutorialBus.emit('scenario:publishFailed', { scenarioId, errors });
                return { published: false, errors };
            }
            console.error('Error publishing scenario:', err);
            const message = err?.response?.data?.message || 'Could not publish this scenario.';
            tutorialBus.emit('scenario:publishFailed', { scenarioId, message });
            return { published: false, message };
        } finally {
            setPublishing(false);
        }
    }, [nodes, edges, flushOps, scenarioId, ops]);

    const unpublish = useCallback(async () => {
        const updated = await ScenarioAPI.updateScenario(scenarioId, {
            isPublished: false,
            clientId: CLIENT_ID,
        });
        setScenario((current) => ({ ...current, ...updated, isPublished: false }));
    }, [scenarioId]);

    const value = useMemo(
        () => ({
            scenario,
            setScenario,
            scenarioId,
            loading,

            // draft graph
            nodes,
            edges,
            setGraph,
            version: ops.version,
            publishedVersion: ops.publishedVersion,
            hasUnpublishedChanges: ops.hasUnpublishedChanges,
            saveState: ops.saveState,
            saveError: ops.saveError,
            hasUnsavedOps: ops.hasUnsavedOps,
            lastRemoteEdit,

            // lookups
            getNode,
            getEdge,
            getOutgoingEdges,
            centerNodeInView,

            // situations
            addSituation,
            addSituationAtCenter,
            updateSituation,
            deleteSituation,
            moveNodes,
            copySituation,

            // choices
            addChoice,
            addSituationWithChoice,
            updateChoice,
            setChoiceTarget,
            reorderChoices,
            deleteChoice,

            // media
            uploadMedia,
            clearMedia,

            // groups and keys (draft state, published on publish)
            groups,
            keys,
            getGroup,
            getKey,
            createGroup,
            updateGroup,
            deleteGroup,
            createKey,
            updateKey,
            deleteKey,

            // table of contents (draft state, creator-only)
            tableOfContents,
            setTableOfContents,

            // publish
            publish,
            unpublish,
            publishing,
            graphErrors,
            setGraphErrors,

            // escape hatch for anything that needs the raw op stream
            dispatchOps,
            flushOps,
        }),
        [
            scenario,
            scenarioId,
            loading,
            nodes,
            edges,
            setGraph,
            ops,
            lastRemoteEdit,
            getNode,
            getEdge,
            getOutgoingEdges,
            centerNodeInView,
            addSituation,
            addSituationAtCenter,
            updateSituation,
            deleteSituation,
            moveNodes,
            copySituation,
            addChoice,
            addSituationWithChoice,
            updateChoice,
            setChoiceTarget,
            reorderChoices,
            deleteChoice,
            uploadMedia,
            clearMedia,
            groups,
            keys,
            getGroup,
            getKey,
            createGroup,
            updateGroup,
            deleteGroup,
            createKey,
            updateKey,
            deleteKey,
            tableOfContents,
            setTableOfContents,
            publish,
            unpublish,
            publishing,
            graphErrors,
            dispatchOps,
            flushOps,
        ]
    );

    return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
};

export const CanvasProvider = ({ children }) => {
    const { scenarioId } = useParams();

    return (
        <EditorUIProvider scenarioId={scenarioId}>
            <CanvasDataProvider>{children}</CanvasDataProvider>
        </EditorUIProvider>
    );
};

export const useCanvas = () => {
    const context = useContext(CanvasContext);
    if (!context) throw new Error('useCanvas must be used inside a CanvasProvider.');
    return context;
};

export default CanvasContext;
